import React, { useCallback } from 'react'
import { CardColor, CardType, GamePhase, GameState, PreparedCard, HeroId, ClassId } from '../types'
import * as gameLogic from '../services/gameLogic'

export const useUiActions = (
  params: {
    gameState: GameState | null,
    setGameState: (u: any) => void,
    localPlayerId: number,
    executeGameAction: (action: any, fn: (state: GameState) => GameState) => void,
    preparedCardRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>,
    setTargeting: (t: { card: PreparedCard; startRect: DOMRect } | null) => void,
    handleActivateCard: (cardId: string, rotation: number, targetId?: number) => void,
    setProtectorChoiceCardId: (cardId: string | null) => void,
    playerIsReady: boolean,
  }
) => {
  const { gameState, setGameState, localPlayerId, executeGameAction, preparedCardRefs, setTargeting, handleActivateCard, setProtectorChoiceCardId, playerIsReady } = params

  const handleChooseHibernationColor = useCallback((color: CardColor) => {
    if (!gameState || gameState.phase !== GamePhase.AWAITING_HIBERNATION_CHOICE) return
    const playerId = gameState.hibernationInitiatorId!
    executeGameAction(
      { type: 'CHOOSE_HIBERNATION_CHOICE', playerId, color },
      (state) => gameLogic.chooseHibernationColor(state, playerId, color)
    )
  }, [gameState, executeGameAction])

  const handleChoosePurificationTarget = useCallback((color: CardColor) => {
    if (!gameState || gameState.phase !== GamePhase.AWAITING_PURIFICATION_TARGET) return
    const playerId = gameState.purificationInitiatorId!
    executeGameAction(
      { type: 'CHOOSE_PURIFICATION_TARGET', playerId, targetColor: color },
      (state) => gameLogic.choosePurificationTarget(state, color)
    )
  }, [gameState, executeGameAction])

  const handleReturnLockedCardFromOverflow = useCallback((cardId: string) => {
    if (!gameState || gameState.phase !== GamePhase.LockedCardOverflow || gameState.overflowPlayerId !== localPlayerId) return
    executeGameAction(
      { type: 'RETURN_LOCKED_CARD_FROM_OVERFLOW', playerId: localPlayerId, cardId },
      (state) => gameLogic.returnLockedCardFromOverflow(state, cardId)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handleOpenTowerChangeMenu = useCallback((towerIndex: number) => {
    if (!gameState) return
    setGameState(gameLogic.openTowerChangeMenu(gameState, localPlayerId, towerIndex))
  }, [gameState, localPlayerId, setGameState])

  const handleCloseTowerChangeMenu = useCallback(() => {
    if (!gameState) return
    setGameState(gameLogic.closeTowerChangeMenu(gameState))
  }, [gameState, setGameState])

  const handleProtectorModeChosen = useCallback((cardId: string, mode: 'ATTACK' | 'DEFENSE') => {
    if (!gameState || !gameState.players) return
    const player = gameState.players.find(p => p.id === localPlayerId)!
    const preparedCard = player.prepared.find(pc => pc.card.id === cardId)
    if (!preparedCard) return
    if (mode === 'DEFENSE') {
      // Если нет активных цепочек — прямая активация как первая цепочка
      const hasActiveChains = gameState.activations && gameState.activations.length > 0
      if (!hasActiveChains) {
        handleActivateCard(cardId, 0)
        return
      }
      // Protector Defense: карта прилипает к курсору для выбора цели
      const cardWithMode = { ...preparedCard, rotation: 0 }
      const cardRect = preparedCardRefs.current[cardId]?.getBoundingClientRect()
      if (cardRect) {
        setTargeting({ card: cardWithMode, startRect: cardRect, source: 'protector-defense' } as any)
      }
    } else {
      const cardWithMode = { ...preparedCard, rotation: 180 }
      const cardRect = preparedCardRefs.current[cardId]?.getBoundingClientRect()
      if (cardRect) {
        setTargeting({ card: cardWithMode, startRect: cardRect, source: 'protector-attack' } as any)
      }
    }
  }, [gameState, localPlayerId, preparedCardRefs, setTargeting, handleActivateCard])

  const handleChooseChromabowColor = useCallback((color: CardColor) => {
    if (!gameState || gameState.phase !== GamePhase.AWAITING_CHROMABOW_CHOICE) return
    const playerId = gameState.chromabowInitiatorId!
    executeGameAction(
      { type: 'CHOOSE_CHROMABOW_COLOR', playerId, color },
      (state) => gameLogic.chooseChromabowColor(state, playerId, color)
    )
  }, [gameState, executeGameAction])

  const handleChooseBurglaryCard = useCallback((cardId: string) => {
    if (!gameState || gameState.phase !== GamePhase.AWAITING_BURGLARY_CHOICE) return
    const playerId = gameState.burglaryInitiatorId!
    executeGameAction(
      { type: 'CHOOSE_BURGLARY_CARD', playerId, cardId },
      (state) => gameLogic.chooseBurglaryCard(state, playerId, cardId)
    )
  }, [gameState, executeGameAction])

  const handleChooseTower = useCallback((towerColor: CardColor) => {
    if (!gameState || gameState.phase !== GamePhase.TowerChoice) return
    executeGameAction(
      { type: 'CHOOSE_TOWER', playerId: localPlayerId, towerColor },
      (state) => gameLogic.chooseTower(state, localPlayerId, towerColor)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handleStashChoice = useCallback((cardId: string) => {
    if (!gameState || gameState.phase !== GamePhase.StashChoice) return
    executeGameAction(
      { type: 'CHOOSE_STASH', playerId: localPlayerId, cardId },
      (state) => gameLogic.makeStashChoice(state, localPlayerId, cardId)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handleChooseHeroClass = useCallback((hero: HeroId, playerClass: ClassId) => {
    if (!gameState || gameState.phase !== GamePhase.TowerChoice) return
    executeGameAction(
      { type: 'CHOOSE_HERO_CLASS', playerId: localPlayerId, hero, playerClass },
      (state) => gameLogic.chooseHeroClass(state, localPlayerId, hero, playerClass)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handleChooseLorianClass = useCallback((playerClass: ClassId) => {
    if (!gameState || gameState.phase !== GamePhase.LorianClassChoice) return
    executeGameAction(
      { type: 'CHOOSE_LORIAN_CLASS', playerId: localPlayerId, playerClass },
      (state) => gameLogic.chooseLorianClass(state, localPlayerId, playerClass)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handleMageCastFromHand = useCallback((cardId: string) => {
    if (!gameState || gameState.phase !== GamePhase.Combat) return
    executeGameAction(
      { type: 'MAGE_CAST_SPELL', playerId: localPlayerId, cardId, rotation: 0 },
      (state) => gameLogic.mageCastSpellFromHand(state, localPlayerId, cardId, 0)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handleMageCastFromHandWithTarget = useCallback((cardId: string, rotation: number, targetId?: number, targetActivationId?: string) => {
    if (!gameState || gameState.phase !== GamePhase.Combat) return
    executeGameAction(
      { type: 'MAGE_CAST_SPELL', playerId: localPlayerId, cardId, rotation, targetId, targetActivationId },
      (state) => gameLogic.mageCastSpellFromHand(state, localPlayerId, cardId, rotation, targetId, targetActivationId)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handleAstridBlastWithTarget = useCallback((color: CardColor, targetId: number) => {
    if (!gameState || (gameState.phase !== GamePhase.Combat && gameState.phase !== GamePhase.TowerPurchase)) return
    if (!gameState.activations || gameState.activations.length > 0) return
    executeGameAction(
      { type: 'ASTRID_BLAST', playerId: localPlayerId, color, targetId },
      (state) => gameLogic.astridHeroBlast(state, localPlayerId, targetId, color)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handleEndDay = useCallback(() => {
    if (!gameState || gameState.phase !== GamePhase.Preparation) return
    executeGameAction(
      { type: 'END_DAY', playerId: localPlayerId },
      (state) => gameLogic.startCombat(state)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handleStartTowerPurchase = useCallback(() => {
    if (!gameState || !gameState.players || gameState.phase !== GamePhase.Combat || gameState.turnPlayerId !== localPlayerId || gameState.players.find(p => p.id === localPlayerId)!.capturedTowerThisTurn) return
    executeGameAction(
      { type: 'START_TOWER_PURCHASE', playerId: localPlayerId },
      (state) => gameLogic.startTowerPurchase(state)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handleCancelTowerPurchase = useCallback(() => {
    if (!gameState || gameState.phase !== GamePhase.TowerPurchase || gameState.turnPlayerId !== localPlayerId) return
    executeGameAction(
      { type: 'CANCEL_TOWER_PURCHASE', playerId: localPlayerId },
      (state) => gameLogic.cancelTowerPurchase(state)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handlePurchaseTower = useCallback((color: CardColor, preferOpponent?: boolean) => {
    if (!gameState || gameState.phase !== GamePhase.TowerPurchase || gameState.turnPlayerId !== localPlayerId) return
    executeGameAction(
      { type: 'PURCHASE_TOWER', playerId: localPlayerId, towerColor: color, preferOpponent },
      (state) => gameLogic.purchaseTower(state, localPlayerId, color, preferOpponent)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handleChangeTowerColor = useCallback((newColor: CardColor) => {
    const menu = gameState?.towerChangeMenu
    if (!gameState || !menu || menu.playerId !== localPlayerId) return
    executeGameAction(
      { type: 'CHANGE_TOWER_COLOR', playerId: localPlayerId, towerIndex: menu.towerIndex, newColor },
      (state) => gameLogic.changeTowerColor(state, newColor)
    )
  }, [gameState, localPlayerId, executeGameAction])

  const handlePreparedCardClick = useCallback((cardId: string, rotation: number) => {
    console.log(`[handlePreparedCardClick DEBUG] cardId=${cardId}, phase=${gameState?.phase}, discardMode=${JSON.stringify(gameState?.discardMode)}`);
    if (!gameState || !gameState.players) return
    const { phase, discardMode } = gameState
    if (discardMode?.[localPlayerId]) return  // FIX: Check player-specific discard mode, not truthy empty object
    const player = gameState.players.find(p => p.id === localPlayerId)!
    const preparedCard = player.prepared.find(pc => pc.card.id === cardId)
    if (!preparedCard) return
    if (phase === GamePhase.Preparation && !playerIsReady) {
      executeGameAction(
        { type: 'RETURN_CARD', playerId: localPlayerId, cardId },
        (state) => gameLogic.returnCardToHand(state, localPlayerId, cardId)
      )
      return
    }
    if (phase === GamePhase.Combat && preparedCard.card.type === CardType.Protector) {
      setProtectorChoiceCardId(cardId)
      return
    }
    console.log(`[handlePreparedCardClick DEBUG] calling handleActivateCard for ${cardId}`);
    handleActivateCard(cardId, rotation)
  }, [gameState, localPlayerId, executeGameAction, handleActivateCard, setProtectorChoiceCardId, playerIsReady])

  return {
    handleChooseHibernationColor,
    handleChooseChromabowColor,
    handleChooseBurglaryCard,
    handleChoosePurificationTarget,
    handleReturnLockedCardFromOverflow,
    handleOpenTowerChangeMenu,
    handleCloseTowerChangeMenu,
    handleProtectorModeChosen,
    handlePreparedCardClick,
    handleChooseTower,
    handleStashChoice,
    handleChooseHeroClass,
    handleChooseLorianClass,
    handleMageCastFromHand,
    handleMageCastFromHandWithTarget,
    handleAstridBlastWithTarget,
    handleEndDay,
    handleStartTowerPurchase,
    handleCancelTowerPurchase,
    handlePurchaseTower,
    handleChangeTowerColor,
  }
}
